import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { encrypt, isSameDay } from "../../../utils/helpers";
import { env } from "../../../env";
import { toast } from "react-toastify";
import { Singleton } from "../Singleton";

const showHash = env.REACT_HASH ?? true;
const print_log = env.PRINT_LOG ?? false;

const getCookie = (name: string): string => {
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return cookie ? decodeURIComponent(cookie.split("=")[1]) : "";
};

const SECURITY_METHOD_HASH =
  "com.advantage.bean.securitymanager.PublicKeyInfo_getPublicKeyForApiRest_String";

let securityKeyPromise: Promise<string | null> | null = null;
let resolveSecurityKey: ((key: string | null) => void) | null = null;

const ensureSecurityKeyPromise = (): Promise<string | null> => {
  if (!securityKeyPromise) {
    securityKeyPromise = new Promise<string | null>((resolve) => {
      resolveSecurityKey = (key) => {
        resolve(key);
        securityKeyPromise = null;
        resolveSecurityKey = null;
      };
    });
  }
  return securityKeyPromise;
};

const triggerSecurityKeyFetch = (axiosInstance: AxiosInstance): Promise<string | null> => {
  const promise = ensureSecurityKeyPromise();
  const url = env.REACT_APP_ENDPOINT + env.REACT_APP_DEPLOY + env.REACT_APP_SERVICE;
  const dataObj = {
    ServiceName: "OrangeCore",
    MethodHash: SECURITY_METHOD_HASH,
    ArgumentList: [null],
  };

  axiosInstance.post(url, JSON.stringify(dataObj)).catch((err) => {
    console.error("Error al obtener la clave de seguridad automáticamente:", err);
    if (resolveSecurityKey) {
      resolveSecurityKey(null);
    }
  });

  return promise;
};

const initialization = (config: AxiosRequestConfig): AxiosInstance => {
  const axiosInstance = axios.create(config);

  axiosInstance.interceptors.request.use(async (request: any) => {
    if (!(request.data instanceof FormData)) {
      try {
        let data: any = null;
        if (typeof request.data === "string") {
          try {
            data = JSON.parse(request.data);
          } catch (e) {
            data = null;
          }
        } else if (typeof request.data === "object" && request.data !== null) {
          data = request.data;
        }

        if (!data || !data.MethodHash) return request;

        const originalHash = data.MethodHash;

        // Si es la llamada del securitymanager propiamente dicha, no la bloqueamos
        if (originalHash === SECURITY_METHOD_HASH) {
          ensureSecurityKeyPromise();
          return request;
        }

        // Para cualquier otro servicio, se debe esperar a que el securitymanager responda y guarde el 'yek'
        let publicKey = sessionStorage.getItem("yek");
        const yekDate = sessionStorage.getItem("yekDate");

        // Si existe la clave pero yekDate no es una fecha que coincida con la fecha actual, invalidamos y pedimos una nueva
        if (publicKey && !isSameDay(yekDate)) {
          sessionStorage.removeItem("yek");
          sessionStorage.removeItem("yekDate");
          Singleton.getInstance().setSessionItem("yek", "");
          Singleton.getInstance().setSessionItem("yekDate", "");
          publicKey = null;
        }

        if (!publicKey) {
          if (securityKeyPromise) {
            publicKey = await securityKeyPromise;
          } else {
            publicKey = await triggerSecurityKeyFetch(axiosInstance);
          }
        }

        if (publicKey) {
          const encryptedHash = encrypt(publicKey, originalHash);
          if (encryptedHash !== "false") {
            data.MethodHash = encryptedHash;
            request.data = typeof request.data === "string" ? JSON.stringify(data) : data;
          }
        }

        if (print_log && showHash) {
          console.group("==> Request");
          console.log("Service:", data.ServiceName);
          console.log("Method:", originalHash);
          console.log("Arguments:", data.ArgumentList);
          console.groupEnd();
        }

        const authToken = sessionStorage.getItem("auth");
        if (authToken) {
          request.headers["Authorization"] = authToken;
        }
      } catch (e) {
        // No es JSON válido o hubo un error al parsear, no modificamos nada
      }
    }

    return request;
  });

  const extractUsefulLines = (htmlMessage: string | undefined): string => {
    if (!htmlMessage) return "Error desconocido";

    const lines = htmlMessage
      .split(/<br\s*\/?>/i)
      .map((line) => line.trim())
      .filter(
        (line) =>
          line &&
          !line.includes("java.") &&
          !line.includes("org.apache.") &&
          !line.includes("jakarta.") &&
          !line.includes("com.advantage.") &&
          !line.includes("Current Thread =>") &&
          !line.includes("bundle.js:")
      );

    return lines.join("\n");
  };

  axiosInstance.interceptors.response.use(
    (response) => {
      try {
        const reqData =
          typeof response.config?.data === "string"
            ? JSON.parse(response.config.data)
            : response.config?.data;

        if (reqData?.MethodHash === SECURITY_METHOD_HASH) {
          const key = response.data?.DataBeanProperties?.ObjectValue?.DataBeanProperties?.Key;
          if (key) {
            sessionStorage.setItem("yek", key);
            sessionStorage.setItem("yekDate", Singleton.getInstance().getCurrenDate());
            Singleton.getInstance().setSessionItem("yek", key);
            if (resolveSecurityKey) {
              resolveSecurityKey(key);
            }
          } else {
            if (resolveSecurityKey) {
              resolveSecurityKey(null);
            }
          }
        }
      } catch (e) {
        // Ignorar error al parsear respuesta
      }

      return response;
    },
    (error) => {
      Singleton.getInstance().spinner(false);

      try {
        const reqData =
          typeof error.config?.data === "string"
            ? JSON.parse(error.config.data)
            : error.config?.data;

        if (reqData?.MethodHash === SECURITY_METHOD_HASH) {
          if (resolveSecurityKey) {
            resolveSecurityKey(null);
          }
        }
      } catch (e) {
        // Ignorar error al parsear respuesta de error
      }

      const status = error.response?.status;
      const rawMessage = error.response?.data?.DataBeanProperties?.ErrorMessage;
      const cleanedMessage = rawMessage || "";

      switch (status) {
        case 400:
          toast.error(cleanedMessage);
          break;
        case 401:
          toast.error("No autorizado.");
          break;
        case 403:
          toast.error("Acceso denegado.");
          break;
        case 404:
          toast.error("Recurso no encontrado.");
          break;
        case 500:
          console.log("Error interno del servidor.");
          break;
        default:
          {
            if (cleanedMessage != "")
              console.warn(cleanedMessage);
          }
          break;
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export default initialization;
